// Bildschirm-Überwachung: Typen und Abfragen (Alarme, Alarm-Einstellungen, Befehle, Live-Screenshots, SMTP).
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './client';
import { useMe, useTenantQuery } from './queries';

export type AlertKind = 'offline' | 'error';
export type CommandType = 'sync' | 'reloadEngine' | 'screenshot';
export type CommandStatus = 'pending' | 'sent' | 'succeeded' | 'failed' | 'expired';

export interface MonitoringSettings {
  alertsEnabled: boolean;
  recipients: string[];
  notifyOffline: boolean;
  notifyErrors: boolean;
  notifyRecovery: boolean;
  offlineDelayMinutes: number;
  errorResolveMinutes: number;
  quietHours: { from: string; until: string } | null;
  digestEnabled: boolean;
  digestHour: number;
  locale: 'de' | 'en';
  screenshotIntervalMinutes: number;
  screenshotRetentionHours: number;
  rowVersion: number;
  smtpConfigured: boolean;
  timezone: string;
}

export interface Alert {
  id: string;
  deviceId: string;
  deviceName: string;
  kind: AlertKind;
  status: 'open' | 'resolved';
  openedAt: string;
  since: string;
  lastEventAt: string;
  occurrences: number;
  code: string | null;
  message: string | null;
  muted: boolean;
  resolvedAt: string | null;
  resolution: 'recovered' | 'timeout' | 'manual' | 'device_removed' | null;
  notifiedAt: string | null;
  recoveryNotifiedAt: string | null;
}

export interface Screenshot {
  id: string;
  deviceId: string;
  source: 'user' | 'schedule';
  mimeType: string;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  capturedAt: string;
  receivedAt: string;
  url: string;
}

export interface Command {
  id: string;
  type: CommandType;
  status: CommandStatus;
  source: 'user' | 'schedule';
  createdAt: string;
  sentAt: string | null;
  completedAt: string | null;
  expiresAt: string;
  message: string | null;
}

export interface OverviewItem {
  deviceId: string;
  alert: { kind: AlertKind; since: string; code: string | null } | null;
  muted: boolean;
  latestScreenshot: Screenshot | null;
}

export interface DeviceMonitoring {
  mute: { mutedUntil: string | null } | null;
  alerts: Alert[];
  screenshots: Screenshot[];
  lastScreenshotCommand: Command | null;
  screenshotIntervalMinutes: number;
  screenshotRetentionHours: number;
}

export interface SmtpSettings {
  source: 'env' | 'settings' | 'none';
  host: string | null;
  port: number | null;
  security: 'starttls' | 'tls' | 'none' | null;
  username: string | null;
  hasPassword: boolean;
  fromAddress: string | null;
  updatedAt: string | null;
  canStorePassword: boolean;
}

export const mk = {
  overview: ['monitoring', 'overview'] as const,
  device: (id: string) => ['monitoring', 'device', id] as const,
  alerts: (f: string) => ['monitoring', 'alerts', f] as const,
  settings: ['monitoring', 'settings'] as const,
  smtp: ['platform-smtp'] as const,
};

const OPEN: CommandStatus[] = ['pending', 'sent'];

/** Status, Alarm und letzter Screenshot aller Bildschirme (für Kacheln). */
export const useMonitoringOverview = (enabled = true) =>
  useTenantQuery(mk.overview, () => api.get<{ items: OverviewItem[]; openAlerts: number }>('/monitoring/overview'), { refetchInterval: 30_000, enabled });

/** Überwachung eines Bildschirms; fragt öfter nach, solange ein Screenshot angefordert ist. */
export const useDeviceMonitoring = (id: string, enabled = true) =>
  useTenantQuery(mk.device(id), () => api.get<DeviceMonitoring>(`/devices/${id}/monitoring`), {
    enabled,
    refetchInterval: (d) => (d?.lastScreenshotCommand && OPEN.includes(d.lastScreenshotCommand.status) ? 2000 : 30_000),
  });

export interface AlertFilter { status?: string; kind?: string; deviceId?: string }
const filterKey = (f: AlertFilter) => new URLSearchParams(Object.entries(f).filter(([, v]) => !!v) as [string, string][]).toString();

/** Alarmverlauf (seitenweise, neueste zuerst). */
export function useAlerts(filter: AlertFilter) {
  const key = filterKey(filter);
  const [pages, setPages] = useState<{ items: Alert[]; nextCursor: string | null }[]>([]);
  const first = useTenantQuery(mk.alerts(key), () => api.get<{ items: Alert[]; nextCursor: string | null }>(`/monitoring/alerts?limit=50${key ? `&${key}` : ''}`), { refetchInterval: 30_000 });
  useEffect(() => setPages([]), [key, first.dataUpdatedAt]);
  const all = [first.data, ...pages].filter(Boolean) as { items: Alert[]; nextCursor: string | null }[];
  const next = all[all.length - 1]?.nextCursor ?? null;
  const more = useCallback(async () => {
    if (!next) return;
    const p = await api.get<{ items: Alert[]; nextCursor: string | null }>(`/monitoring/alerts?limit=50&cursor=${encodeURIComponent(next)}${key ? `&${key}` : ''}`);
    setPages((x) => [...x, p]);
  }, [next, key]);
  return { items: all.flatMap((p) => p.items), isLoading: first.isLoading, hasMore: !!next, more };
}

export const useMonitoringSettings = (enabled = true) => useTenantQuery(mk.settings, () => api.get<MonitoringSettings>('/monitoring/settings'), { enabled });

export function useSmtpSettings(enabled = true) {
  const { data: me } = useMe();
  return useQuery({ queryKey: mk.smtp, queryFn: () => api.get<SmtpSettings>('/platform/smtp'), enabled: enabled && !!me?.permissions.includes('system.settings') });
}

/**
 * Befehl an einen Bildschirm senden und auf das Ergebnis warten (Abfrage alle 1,5 s, höchstens timeoutMs).
 * Liefert den letzten bekannten Stand (auch wenn noch offen).
 */
export async function runCommand(deviceId: string, type: CommandType, o: { timeoutMs?: number; signal?: { aborted: boolean } } = {}): Promise<Command> {
  const { commandId } = await api.post<{ commandId: string; status: CommandStatus }>(`/devices/${deviceId}/commands`, { type });
  const until = Date.now() + (o.timeoutMs ?? 60_000);
  for (;;) {
    const c = await api.get<Command>(`/devices/${deviceId}/commands/${commandId}`);
    if (!OPEN.includes(c.status) || Date.now() > until || o.signal?.aborted) return c;
    await new Promise((r) => setTimeout(r, 1500));
  }
}

/** Screenshot anfordern: Zustand für Knopf und Hinweise; aktualisiert die Überwachungsdaten nach Abschluss. */
export function useScreenshotRequest(deviceId: string) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Command | null>(null);
  const [error, setError] = useState<unknown>(null);
  const abort = useRef({ aborted: false });
  useEffect(() => () => {
    abort.current.aborted = true;
  }, []);
  const request = useCallback(async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      void qc.invalidateQueries({ queryKey: mk.device(deviceId) });
      const c = await runCommand(deviceId, 'screenshot', { timeoutMs: 90_000, signal: abort.current });
      setResult(c);
      await Promise.all([qc.invalidateQueries({ queryKey: mk.device(deviceId) }), qc.invalidateQueries({ queryKey: mk.overview })]);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }, [deviceId, qc]);
  return { busy, result, error, request };
}
